// @flow

import React from 'react';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import type { ContextRouter } from 'react-router-dom';
import { useSimpalsBar, useSimpalsBarCounters } from '@simpals/next-simpalsbar/dist/react';
import { IS_STAGING } from '../../constants';
import LocaleCode from '../../providers/LocaleCode';

export const AVATAR_URL = 'https://i.simpalsmedia.com/forum.md/avatars/200x200';
export const NO_AVATAR = `${AVATAR_URL}/00000000-0000-0000-0000-000000000000.png`;

const TopbarContainer = styled.div`
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  text-align: right;
  padding-right: 16px;
  height: 36px;
  background: #474d59;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionsGroup = styled.div`
  display: flex;
  gap: 4px;
  margin-right: 12px;
`;

const UserInfo = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Username = styled.span`
  font-weight: 600;
  font-size: 14px;
  margin-right: 8px;
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
`;

const IconButton = styled.button`
  background-color: transparent;
  color: white;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  gap: 6px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
    opacity: 0.8;
    flex-shrink: 0;
  }

  &:hover img {
    opacity: 1;
  }
`;

const WalletAmount = styled.span`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LanguageSwitcher = styled.button`
  background-color: transparent;
  color: white;
  border: none;
  padding: 4px 8px;
  margin-left: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  transition: all 0.2s;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const LogoutButton = styled(IconButton)`
  margin-left: 8px;
`;

const LoginButton = styled.button`
  background-color: #2185d0;
  color: white;
  border: 1px solid transparent;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #1678c2;
  }
`;

function Topbar() {
  const { account } = useSimpalsBar({ isDev: IS_STAGING });
  const { counters } = useSimpalsBarCounters({
    pollInterval: 10000,
    counters: ["chat", "notifications", "wallet"],
  });

  const handleLogin = () => {
    if (window.TOPBAR) {
      window.TOPBAR.login({ allowDev: IS_STAGING });
    }
  };

  const handleLogout = () => {
    if (window.TOPBAR) {
      window.TOPBAR.logout();
    }
  };

  const handleOpenSettings = () => {
    if (window.TOPBAR) {
      window.TOPBAR.openSettings();
    }
  };

  const handleOpenChat = () => {
    if (window.TOPBAR) {
      window.TOPBAR.openChat();
    }
  };

  const handleOpenWallet = () => {
    if (window.TOPBAR) {
      window.TOPBAR.openWallet();
    }
  };

  const handleOpenNotifications = () => {
    if (window.TOPBAR) {
      window.TOPBAR.openNotifications();
    }
  };

  const walletBalance = counters?.wallet ?? 0;
  const formattedBalance = new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(walletBalance);
  return (
    <LocaleCode>
      {(localeCode: string) => {
        const handleLanguageSwitch = () => {
          const newLang = localeCode === 'ro' ? 'ru' : 'ro';
          // window.TOPBAR.setLang(newLang);
          if (window.TOPBAR) {
            window.TOPBAR.changeLanguage(newLang).then(() => {
              window.location.reload();
            }).catch((error) => {
              console.error('Error changing language:', error);
            });
          }
        };

        return (
          <TopbarContainer>
            {account ? (
              <>
                <ActionsGroup>
                  <IconButton onClick={handleOpenChat} title="Чат">
                    <img src="/icons/chat-icon.svg" alt="Чат" />
                  </IconButton>
                  <IconButton onClick={handleOpenWallet} title="Кошелек">
                    <img src="/icons/wallet-icon.svg" alt="Кошелек" />
                    <WalletAmount>{formattedBalance} MDL</WalletAmount>
                  </IconButton>
                  <IconButton onClick={handleOpenNotifications} title="Уведомления">
                    <img src="/icons/notifications-icon.svg" alt="Уведомления" />
                  </IconButton>
                </ActionsGroup>
                <UserInfo onClick={handleOpenSettings}>
                  <Username>{account.login}</Username>
                  <Avatar
                    src={account.avatar ? `${AVATAR_URL}/${account.avatar}` : NO_AVATAR}
                    alt={account.login}
                  />
                </UserInfo>
                <LanguageSwitcher onClick={handleLanguageSwitch} title="Сменить язык">
                  {localeCode === 'ro' ? 'RO' : 'RU'}
                </LanguageSwitcher>
                <LogoutButton onClick={handleLogout} title="Выйти">
                  ⎋
                </LogoutButton>
              </>
            ) : (
              <>
                <LanguageSwitcher onClick={handleLanguageSwitch} title="Сменить язык">
                  {localeCode === 'ro' ? 'RO' : 'RU'}
                </LanguageSwitcher>
                <LoginButton onClick={handleLogin}>
                  Войти
                </LoginButton>
              </>
            )}
          </TopbarContainer>
        );
      }}
    </LocaleCode>
  );
}

export default Topbar;
